import { AreaUnit, DistanceUnit, formattedDistanceUnit, isUsable, LineWidth } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { useMemo } from "react"
import { LegendItem, LegendItemProps } from "./useLegendItems"
import * as Icons from "@nova/component-icons"
import { coordToLngLat } from "features/entity-caching/fire-perimeters/module"
import { selectOrthoImagesByFlightId } from "features/entity-caching/images/orthos/selectors"
import {
    selectActiveFirePerimeterPolygons,
    selectAllFirePerimeterBufferLines,
} from "features/entity-caching/fire-perimeters/selectors"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { convertDistance, formatArea, formatDistance } from "@nova/format"
import { Coordinate, Polygon } from "@nova/protobuf"
import { selectInfraredImagesByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { config } from "@nova/configuration/browser"
import { selectLegendPosition, selectPDFLegendSize } from "plugins/PDFv2/PDFSlice"
import { isLegendItemInPDFSnapshot } from "./module"
import {
    selectInfraredFlightById,
    selectOrthoFlightById,
    selectShowLayerByIdMapForFlightPaths,
    selectShowLayerByIdMapForHotspotScanArea,
    selectShowLayerByIdMapForOrthoScanArea,
} from "features/entity-caching/flights/selectors"
import { useViewport } from "features/map/GlobalViewState"
import { getAncestors } from "panels/content/side-panel/utils"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"

export const useKMLLegendItems = ({ offsetX, offsetY, width, height }: LegendItemProps) => {
    const flightPaths = useAppSelector(selectShowLayerByIdMapForFlightPaths)
    const activeFlightPaths = Object.keys(flightPaths).filter((key) => flightPaths[key])
    const infraredScanAreas = useAppSelector(selectShowLayerByIdMapForHotspotScanArea)
    const activeInfraredScanAreas = Object.keys(infraredScanAreas).filter(
        (key) => infraredScanAreas[key]
    )
    const view = useViewport()
    const orthoScanAreas = useAppSelector(selectShowLayerByIdMapForOrthoScanArea)
    const activeOrthoScanAreas = Object.keys(orthoScanAreas).filter((key) => orthoScanAreas[key])
    const polygons = useAppSelector(selectActiveFirePerimeterPolygons)
    const bufferLines = useAppSelector(selectAllFirePerimeterBufferLines)
    const legendSize = useAppSelector(selectPDFLegendSize())
    const legendPosition = useAppSelector(selectLegendPosition())

    const { organizationSettingArea } = config.use("organizationSettingArea") as {
        organizationSettingArea: AreaUnit
    }
    const { organizationSettingDistance } = config.use("organizationSettingDistance") as {
        organizationSettingDistance: DistanceUnit
    }

    const showFlightPathLegendItem = useMemo(() => {
        const state = store.getState()
        return activeFlightPaths.some((id) => {
            const images =
                selectInfraredImagesByFlightId(id)(state).length > 0
                    ? selectInfraredImagesByFlightId(id)(state)
                    : selectOrthoImagesByFlightId(id)(state)

            return images?.some((i) => {
                const image = isUsable(i)
                if (image) {
                    try {
                        const [x, y] = view.project([image.longitude!, image.latitude!])
                        const isInsideSnapshot = isLegendItemInPDFSnapshot(
                            x,
                            y,
                            {
                                offsetX,
                                offsetY,
                                width,
                                height,
                            },
                            legendSize,
                            legendPosition
                        )
                        return isInsideSnapshot
                    } catch (err) {
                        console.error(err)
                    }
                }
            })
        })
    }, [activeFlightPaths, view, offsetX, offsetY, width, height, legendSize, legendPosition])

    const entityMap = useAppSelector(selectAllEntitiesMap)
    const infraredScanAreaBuffersInView = useMemo(() => {
        const state = store.getState()
        return activeInfraredScanAreas.reduce(
            (uniqueScanAreas, scanArea) => {
                const flight = selectInfraredFlightById(scanArea)(state)
                if (!flight || flight.archivedTimestamp) return uniqueScanAreas
                const mergedFlight = flight.mergeId ? entityMap[flight.mergeId] : undefined
                if (
                    !flight.visible ||
                    !!getAncestors(flight.id, entityMap).find((ancestor) => !ancestor.visible)
                ) {
                    return uniqueScanAreas
                }
                if (
                    mergedFlight &&
                    (!mergedFlight.visible ||
                        !!getAncestors(mergedFlight.id, entityMap).find(
                            (ancestor) => !ancestor.visible
                        ))
                ) {
                    return uniqueScanAreas
                }
                const isWithinSnapshot = flight.job?.scanArea.some((point) => {
                    try {
                        const [x, y] = view.project(coordToLngLat(point))
                        const isInsideSnapshot = isLegendItemInPDFSnapshot(
                            x,
                            y,
                            {
                                offsetX,
                                offsetY,
                                width,
                                height,
                            },
                            legendSize,
                            legendPosition
                        )
                        return isInsideSnapshot
                    } catch (err) {
                        console.error(err)
                    }
                })
                if (!isWithinSnapshot) return uniqueScanAreas
                if (mergedFlight) {
                    const existingIndex = uniqueScanAreas.findIndex(
                        (scanArea) => scanArea.id === `scan-area-${mergedFlight.id}`
                    )
                    if (existingIndex === -1) {
                        uniqueScanAreas.push({
                            id: `scan-area-${mergedFlight.id}`,
                            title: `${mergedFlight.name} Scan Area`,
                            coordinates: flight.job?.scanArea || [],
                            fillColor: flight.scanAreaFillColor || 0,
                            outlineColor: flight.scanAreaOutlineColor || 0,
                            area: 0, // hide the ui option to see area, we can't just add areas in case there is overlap
                        })
                    }
                } else {
                    uniqueScanAreas.push({
                        id: `scan-area-${flight.id}`,
                        title: `${flight.name} Scan Area`,
                        coordinates: flight.job?.scanArea || [],
                        fillColor: flight.scanAreaFillColor || 0,
                        outlineColor: flight.scanAreaOutlineColor || 0,
                        area: flight.job?.area || 0,
                    })
                }
                return uniqueScanAreas
            },
            [] as {
                id: string
                title: string
                coordinates: Coordinate[]
                fillColor: number
                outlineColor: number
                area: number
            }[]
        )
    }, [
        activeInfraredScanAreas,
        entityMap,
        height,
        legendPosition,
        legendSize,
        offsetX,
        offsetY,
        view,
        width,
    ])

    const orthoScanAreaBuffersInView = useMemo(() => {
        const state = store.getState()
        return activeOrthoScanAreas.reduce(
            (uniqueScanAreas, scanArea) => {
                const flight = selectOrthoFlightById(scanArea)(state)
                if (!flight) return uniqueScanAreas
                if (
                    !flight.visible ||
                    !!getAncestors(flight.id, entityMap).find((ancestor) => !ancestor.visible)
                )
                    return uniqueScanAreas
                const isWithinSnapshot = flight.job?.scanArea.some((point) => {
                    try {
                        const [x, y] = view.project(coordToLngLat(point))
                        const isInsideSnapshot = isLegendItemInPDFSnapshot(
                            x,
                            y,
                            {
                                offsetX,
                                offsetY,
                                width,
                                height,
                            },
                            legendSize,
                            legendPosition
                        )
                        return isInsideSnapshot
                    } catch (err) {
                        console.error(err)
                    }
                })
                if (!isWithinSnapshot) return uniqueScanAreas
                uniqueScanAreas.push({
                    id: `scan-area-${flight.id}`,
                    title: `${flight.name} Scan Area`,
                    coordinates: flight.job?.scanArea || [],
                    fillColor: flight.scanAreaFillColor || 0,
                    outlineColor: flight.scanAreaOutlineColor || 0,
                    area: flight.job?.area || 0,
                })
                return uniqueScanAreas
            },
            [] as {
                id: string
                title: string
                coordinates: Coordinate[]
                fillColor: number
                outlineColor: number
                area: number
            }[]
        )
    }, [
        activeOrthoScanAreas,
        entityMap,
        height,
        legendPosition,
        legendSize,
        offsetX,
        offsetY,
        view,
        width,
    ])

    const uniquePerimetersInView = useMemo(() => {
        const seenPerimeters: Set<string> = new Set()

        return polygons.reduce(
            (uniquePolygons, polygon) => {
                const setKey = `${polygon.color}-${polygon.name}`
                if (seenPerimeters.has(setKey)) return uniquePolygons

                const isWithinSnapshot = polygon.coordinates.some((point) => {
                    try {
                        const [x, y] = view.project(coordToLngLat(point))
                        const isInsideSnapshot = isLegendItemInPDFSnapshot(
                            x,
                            y,
                            {
                                offsetX,
                                offsetY,
                                width,
                                height,
                            },
                            legendSize,
                            legendPosition
                        )
                        return isInsideSnapshot
                    } catch (err) {
                        console.error(err)
                    }
                })

                if (!isWithinSnapshot) return uniquePolygons

                seenPerimeters.add(setKey)
                uniquePolygons.push(polygon)

                return uniquePolygons
            },
            [] as (Polygon & { color: number; width: LineWidth; name: string })[]
        )
    }, [polygons, view, offsetX, offsetY, width, height, legendSize, legendPosition])

    const uniquePerimeterBuffersInView = useMemo(() => {
        const colorsByDistance: Record<string, Set<number>> = {}

        return bufferLines.reduce(
            (uniqueBufferLines, buffer) => {
                if (!buffer.color) return uniqueBufferLines

                const distanceKey = buffer.distance.toFixed(1)
                colorsByDistance[distanceKey] ??= new Set()

                if (colorsByDistance[distanceKey].has(buffer.color)) return uniqueBufferLines

                const isWithinSnapshot = buffer.coordinates.some((point) => {
                    try {
                        const [x, y] = view.project(coordToLngLat(point))
                        const isInsideSnapshot = isLegendItemInPDFSnapshot(
                            x,
                            y,
                            {
                                offsetX,
                                offsetY,
                                width,
                                height,
                            },
                            legendSize,
                            legendPosition
                        )
                        return isInsideSnapshot
                    } catch (err) {
                        console.error(err)
                    }
                })

                if (!isWithinSnapshot) return uniqueBufferLines

                colorsByDistance[distanceKey].add(buffer.color)
                uniqueBufferLines.push({
                    color: buffer.color,
                    distance: buffer.distance,
                    unit: buffer.unit,
                    width: buffer.width,
                })

                return uniqueBufferLines
            },
            [] as { color: number; distance: number; unit: DistanceUnit; width: LineWidth }[]
        )
    }, [bufferLines, view, offsetX, offsetY, width, height, legendSize, legendPosition])

    const kmlLegendItems = useMemo(() => {
        const legendItems: LegendItem[] = []
        if (showFlightPathLegendItem) {
            legendItems.push({
                icon: <Icons.FlightPath />,
                title: "Flight Path",
                id: "flightpath",
            })
        }
        uniquePerimetersInView.forEach((polygon) => {
            const color = decodeNumberToColor(polygon.color)
            const displayName = polygon.name.replace(/\.[^/.]+$/, "")
            legendItems.push({
                icon:
                    polygon.width !== LineWidth.thick ? (
                        <Icons.Perimeter style={{ color }} />
                    ) : (
                        <Icons.PerimeterThick style={{ color }} />
                    ),
                title: displayName,
                id: `perimeter-${polygon.color}-${polygon.name}`,
            })
        })
        uniquePerimeterBuffersInView.forEach((buffer) => {
            const formattedDistance = Math.round(
                convertDistance(buffer.distance, DistanceUnit.Meters, buffer.unit)
            )
            const color = decodeNumberToColor(buffer.color)

            legendItems.push({
                icon:
                    buffer.width !== LineWidth.thick ? (
                        <Icons.Perimeter style={{ color }} />
                    ) : (
                        <Icons.PerimeterThick style={{ color }} />
                    ),
                title: `Buffer Line ${formattedDistance} ${formattedDistanceUnit[buffer.unit].abbrev}`,
                id: `buffer-${buffer.distance.toFixed(1)}-${buffer.color}`,
                stat: {
                    label: "Distance",
                    stat: formatDistance({
                        value: buffer.distance,
                        unit: organizationSettingDistance,
                    }),
                },
            })
        })
        infraredScanAreaBuffersInView.forEach((buffer) => {
            const outlineColor = decodeNumberToColor(buffer.outlineColor)
            const fillColor = decodeNumberToColor(buffer.fillColor)
            legendItems.push({
                icon: <Icons.Perimeter style={{ color: outlineColor, fill: fillColor }} />,
                title: buffer.title,
                id: buffer.id,
                stat: !!buffer.area
                    ? {
                          label: "Area",
                          stat: formatArea({
                              value: buffer.area,
                              unit: organizationSettingArea,
                          }),
                      }
                    : undefined,
            })
        })
        orthoScanAreaBuffersInView.forEach((buffer) => {
            const outlineColor = decodeNumberToColor(buffer.outlineColor)
            const fillColor = decodeNumberToColor(buffer.fillColor)
            legendItems.push({
                icon: <Icons.Perimeter style={{ color: outlineColor, fill: fillColor }} />,
                title: buffer.title,
                id: buffer.id,
                stat: {
                    label: "Area",
                    stat: formatArea({
                        value: buffer.area,
                        unit: organizationSettingArea,
                    }),
                },
            })
        })

        return legendItems
    }, [
        showFlightPathLegendItem,
        uniquePerimetersInView,
        uniquePerimeterBuffersInView,
        infraredScanAreaBuffersInView,
        orthoScanAreaBuffersInView,
        organizationSettingArea,
        organizationSettingDistance,
    ])
    return kmlLegendItems
}
